import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { CTASection } from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { ServiceCard } from "@/components/ServiceCard";
import { Link } from "react-router-dom";
import { 
  ArrowRight, CheckCircle2, ChevronDown, Syringe, Smile, Brackets, Sun, Stethoscope
} from "lucide-react";
import { FAQAccordion } from "@/components/FAQAccordion";
import { PageTitle } from "@/components/PageTitle";

const Services = () => {
  const [activeCategory, setActiveCategory] = useState<string | null>("all");
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBookAppointment = () => {
    window.open("https://www.healthcoco.com/nagpur/doctor/dr-hemant-asrani-endodontist-dentist/USRH249D644", "_blank");
  };

  // Service categories for the filter
  const serviceCategories = [
    { id: "all", name: "All Services" },
    { id: "pain-relief", name: "Pain Relief", icon: <Syringe size={18} className="mr-2" /> },
    { id: "preventive", name: "Preventive Care", icon: <Stethoscope size={18} className="mr-2" /> },
    { id: "cosmetic", name: "Cosmetic", icon: <Smile size={18} className="mr-2" /> },
    { id: "restorative", name: "Restorative", icon: <Sun size={18} className="mr-2" /> },
    { id: "pediatric", name: "Pediatric", icon: <Smile size={18} className="mr-2" /> },
    { id: "general", name: "General", icon: <Brackets size={18} className="mr-2" /> },
  ];

  // All services organized by category
  const services = [
    {
      category: "pain-relief",
      items: [
        { 
          title: "Painless RCT",
          description: "Advanced root canal treatments using the latest techniques and anesthesia to ensure a comfortable, pain-free experience.",
          link: "/services/painless-rct-in-nagpur",
          imageUrl: "/lovable-uploads/RCT.png"
        },
        { 
          title: "Wisdom Tooth Removal",
          description: "Safe and comfortable extraction of problematic wisdom teeth by experienced dental surgeons.",
          link: "/services/wisdom-tooth-removal",
          imageUrl: "/lovable-uploads/54938cd8-5e52-4eeb-886d-9181303a684c.png"
        },
        { 
          title: "TMJ/TMD Treatment",
          description: "Specialized care for jaw joint disorders and related pain, offering relief and improved function.",
          link: "/services/tmj-tmd",
          imageUrl: "/lovable-uploads/855f973b-22a9-4935-8eac-6853245003fd.png"
        },
        { 
          title: "Single Day RCT",
          description: "Complete root canal treatment in just one visit, saving you time without compromising quality of care.",
          link: "/services/single-day-rct",
          imageUrl: "/lovable-uploads/rct_Q.jpg"
        }
      ]
    },
    {
      category: "preventive",
      items: [
        { 
          title: "Gum Cleaning",
          description: "Professional deep cleaning to remove plaque and tartar, preventing gum disease and maintaining oral health.",
          link: "/services/gum-cleaning-in-nagpur",
          imageUrl: "/lovable-uploads/d1e16655-21d1-4d49-bd07-3de68a958c4e.png"
        },
        { 
          title: "Teeth Cleaning",
          description: "Regular professional cleanings to remove plaque, prevent cavities, and keep your smile bright and healthy.",
          link: "/services/teeth-cleaning",
          imageUrl: "/lovable-uploads/f578e872-9601-4c3f-bca2-0260505ab8a5.png"
        }
      ]
    },
    {
      category: "cosmetic",
      items: [
        { 
          title: "Cosmetic Dentistry",
          description: "Transform your smile with our range of cosmetic treatments for a more confident and beautiful smile.",
          link: "/services/cosmetic-dentistry",
          imageUrl: "/lovable-uploads/Consmetic Dental.png"
        },
        { 
          title: "Orthodontics",
          description: "Achieve a straighter smile with our range of orthodontic treatments including traditional braces.",
          link: "/services/orthodontics",
          imageUrl: "/lovable-uploads/Orthodontics.png"
        },
        { 
          title: "Invisalign",
          description: "Straighten your teeth discreetly with clear, removable aligners customized for your unique smile.",
          link: "/services/invisalign",
          imageUrl: "/lovable-uploads/79bf0209-ea1e-4839-a4a8-34dc4533acb6.png"
        },
        { 
          title: "Smile Makeover",
          description: "Comprehensive transformation of your smile through a personalized combination of cosmetic procedures.",
          link: "/services/smile-makeover",
          imageUrl: "/lovable-uploads/49df8a59-4334-4e73-bcb6-d9838e803286.png"
        },
        { 
          title: "Dental Veneers",
          description: "Thin porcelain shells that cover the front surface of teeth to improve appearance and protect damaged tooth surfaces.",
          link: "/services/dental-veneers",
          imageUrl: "/lovable-uploads/785110c2-97b3-4b07-a330-a8bbc29b8e1c.png"
        },
        { 
          title: "Teeth Whitening",
          description: "Professional whitening treatments that effectively remove stains for a brighter, more radiant smile.",
          link: "/services/tooth-whitening",
          imageUrl: "/lovable-uploads/762aed2d-c93c-4143-bd1e-b77314abdd09.png"
        },
        { 
          title: "Laser Dentistry",
          description: "Advanced laser technology for precise, comfortable treatment of various dental conditions with minimal discomfort.",
          link: "/services/laser-dentistry",
          imageUrl: "/lovable-uploads/a3c0d8d0-fad1-41cf-88e4-d25b7c5d821f.png"
        }
      ]
    },
    {
      category: "restorative",
      items: [
        { 
          title: "Dental Implants",
          description: "Replace missing teeth with state-of-the-art dental implants that look, feel, and function like natural teeth.",
          link: "/services/dental-implants-in-nagpur",
          imageUrl: "/lovable-uploads/Implants.png"
        },
        { 
          title: "Crowns & Bridges",
          description: "Restore damaged teeth or replace missing teeth with durable, natural-looking dental crowns and bridges.",
          link: "/services/crowns-bridges-in-nagpur",
          imageUrl: "/lovable-uploads/41dbeea4-047f-44f4-8002-7151462d749f.png"
        },
        { 
          title: "Full Mouth Rehabilitation",
          description: "Complete restoration of oral health and function with our comprehensive full mouth rehabilitation treatments.",
          link: "/services/full-mouth-implants-in-nagpur",
          imageUrl: "/lovable-uploads/Full Mouth.png"
        }
      ]
    },
    {
      category: "pediatric",
      items: [
        { 
          title: "Pediatric Dentistry",
          description: "Child-friendly dental care in a comfortable environment, establishing good oral health habits early.",
          link: "/services/pediatric-dentistry-in-nagpur",
          imageUrl: "/lovable-uploads/Kids.png"
        }
      ]
    },
    {
      category: "general",
      items: [
        { 
          title: "General Dentistry",
          description: "Comprehensive care for the whole family including exams, cleanings, fillings, and preventive treatments.",
          link: "/services/general-dentistry-in-nagpur",
          imageUrl: "/lovable-uploads/8812467f-0f94-4213-9dc9-88c4b544d302.png"
        }
      ]
    }
  ];

  // Filter services based on active category
  const filteredServices = activeCategory === "all" 
    ? services.flatMap(category => category.items)
    : services.find(category => category.category === activeCategory)?.items || [];

  const generalFaqs = [
    {
      question: "What dental services do you provide?",
      answer: "We offer a comprehensive range of dental services including general dentistry, cosmetic dentistry, orthodontics, full mouth rehabilitation and implants, root canal treatments, and pediatric dentistry."
    },
    {
      question: "How often should I visit the dentist?",
      answer: "We recommend visiting the dentist every six months for routine check-ups and professional cleanings. However, some patients may need more frequent visits based on their oral health needs."
    },
    {
      question: "Do you accept dental insurance?",
      answer: "Yes, we work with many insurance providers. Please contact our office for specific information about your coverage and benefits."
    },
    {
      question: "What should I do in case of a dental emergency?",
      answer: "Call our office immediately. We reserve time in our schedule for emergency patients and will do our best to see you as soon as possible."
    },
    {
      question: "How can I schedule an appointment?",
      answer: "You can schedule an appointment by calling our office, using our online booking system, or visiting our clinic in person."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <PageTitle title="Services – Asrani Dental Clinic" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-dental-offwhite">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <span className="inline-block text-dental-highlight font-medium mb-4 uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold mb-6">
              Comprehensive Dental Solutions
            </h1>
            <p className="text-lg text-dental-muted">
              We offer a wide range of dental services to meet all your oral health needs, from routine check-ups to advanced cosmetic and restorative procedures.
            </p>
          </div>
        </div>
      </section>
      
      {/* Services Filter Section */}
      <section className="py-8 bg-white border-b border-dental-cream sticky top-[72px] z-10">
        <div className="container-custom">
          <div className="flex flex-nowrap overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                className={`flex-shrink-0 flex items-center px-4 py-2 mr-2 rounded-full transition-all ${
                  activeCategory === category.id 
                    ? 'bg-dental-highlight text-white' 
                    : 'bg-dental-offwhite text-dental-text hover:bg-dental-cream'
                }`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.icon && category.icon}
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {filteredServices.map((service) => (
              <ServiceCard 
                key={service.link}
                title={service.title}
                description={service.description}
                link={service.link}
                imageUrl={service.imageUrl}
              />
            ))}
          </div>
          
          {filteredServices.length === 0 && (
            <div className="text-center py-12">
              <p className="text-dental-muted text-lg">No services found in this category.</p>
            </div>
          )}
        </div>
      </section>
      
      {/* Service Features Section */}
      <section className="py-16 md:py-24 bg-dental-cream">
        <div className="container-custom">
          <SectionTitle
            title="Why Choose Our Dental Services"
            subtitle="Our Approach"
            description="At Asrani Dental Clinic, we're committed to providing exceptional dental care that's tailored to your unique needs."
            alignment="center"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12">
            <div className="bg-white p-8 rounded-xl shadow-soft">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 rounded-full bg-dental-highlight/10 flex items-center justify-center mr-4 mt-1">
                  <CheckCircle2 size={20} className="text-dental-highlight" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-medium mb-2">Personalized Treatment Plans</h3>
                  <p className="text-dental-muted">We create customized treatment plans tailored to your specific dental needs and goals, ensuring the best possible outcomes.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-soft">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 rounded-full bg-dental-highlight/10 flex items-center justify-center mr-4 mt-1">
                  <CheckCircle2 size={20} className="text-dental-highlight" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-medium mb-2">State-of-the-Art Technology</h3>
                  <p className="text-dental-muted">We utilize the latest dental technology and techniques to provide efficient, effective, and comfortable treatments.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-soft">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 rounded-full bg-dental-highlight/10 flex items-center justify-center mr-4 mt-1">
                  <CheckCircle2 size={20} className="text-dental-highlight" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-medium mb-2">Experienced Specialists</h3>
                  <p className="text-dental-muted">Our team of specialists brings years of experience and expertise to every procedure, ensuring high-quality care.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-soft">
              <div className="flex items-start mb-4">
                <div className="w-10 h-10 rounded-full bg-dental-highlight/10 flex items-center justify-center mr-4 mt-1">
                  <CheckCircle2 size={20} className="text-dental-highlight" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-medium mb-2">Comprehensive Care</h3>
                  <p className="text-dental-muted">From preventive care to complex restorative procedures, we provide all the dental services you need under one roof.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-custom">
          <SectionTitle
            title="Frequently Asked Questions"
            subtitle="Common Questions"
            description="Find answers to common questions about our dental services and treatments."
            alignment="center"
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <FAQAccordion faqs={generalFaqs} />
          </div>
          
          <div className="text-center mt-12">
            <p className="text-dental-muted mb-4">Don't see your question? Reach out to our team for assistance.</p>
            <Button className="btn-secondary" onClick={() => window.location.href = '/contact'}>
              Contact Us
            </Button>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <CTASection
        title="Ready to Transform Your Smile?"
        description="Schedule your consultation today and take the first step towards the healthy, beautiful smile you deserve."
        primaryButtonText="Book an Appointment"
        secondaryButtonText="Contact Us"
        primaryButtonAction={handleBookAppointment}
        secondaryButtonAction={() => window.location.href = "/contact"}
      />
      
      <Footer />
    </div>
  );
};

export default Services;
